// Plain-language definitions for the mastering/scoring vocabulary, grounded in the
// pipeline (the mastering_* tables and the scoring layer). Keyed by the raw
// value/label used in the UI; the Term component and SectionCard tooltips resolve
// terms through define().
//
// Ported from the standalone Golden Explorer so the wording stays consistent with
// the analysis it came from. Accept-basket copy is derived from each rule's
// dominant signal profile in the Takeda cut — the pipeline's exact rule
// definitions aren't published, so treat those entries as descriptive.
export const GLOSSARY: Record<string, string> = {
  // ---- review-queue item types ----
  conflict:
    "A golden record the pipeline flagged as possibly wrong to have merged — its members disagree enough (distance, fields, or duplicate keys) that it may need splitting.",
  "conflict / split-risk":
    "A golden record whose members may not belong together — flagged as a possible bad merge that might need splitting.",
  pair_review:
    "Two separate golden records the pipeline thinks might be the same real-world site — a candidate merge awaiting a human/judge decision.",
  "pair review":
    "Two separate golden records that might be the same real-world site — a candidate merge awaiting review.",
  pi_review:
    "Two golden investigator (person) records that may be the same individual — a candidate person merge (or a person that may need splitting).",
  "pi dedup":
    "Candidate duplicate investigators — two golden person records that may be the same individual.",
  pre_mastering:
    "A raw scored pair that hasn't been mastered yet — scorer throughput, not a review item.",

  // ---- conflict flag types ----
  SPLIT_RISK:
    "The cluster's members are far apart (e.g. >100 km) or otherwise inconsistent — the merge may have been wrong; consider splitting.",
  FIELD_CONFLICT:
    "Members carry conflicting values for a key field (name/address/phone), so the chosen canonical value is uncertain.",
  GEO_CONFLICT:
    "Member locations disagree geographically — the merged record may span distinct sites.",
  CROSS_FGID_DUP:
    "The same source record appears under more than one facility/golden id — a duplicate to reconcile.",

  // ---- pair-review relationship types (why a pair is borderline) ----
  LIKELY_COLOCATED:
    "Same address/location but likely distinct units — usually kept separate (low merge rate).",
  AMBIGUOUS:
    "Evidence is mixed with no clear signal either way — the hardest middle-ground pairs.",
  PARENT_UNIT:
    "One record is a parent institution, the other a unit/department within it — usually kept separate (a unit is not its parent).",
  STRONG_SAME_NAME:
    "Names match strongly — a likely same-site merge (high merge rate).",
  SEED_OVERRULE:
    "A trusted seed record disagrees with the scorer; review decides whether the seed overrules.",
  SEED_OVERRULE_SAME_POSTAL:
    "A trusted seed disagrees with the scorer despite a shared postal code; review decides.",
  FACILITY_DEDUP_POSTAL_CONFLICT:
    "Facility-level duplicate candidates whose postal codes conflict — needs a human call.",
  PI_MEDIATED_MERGE:
    "Linked because they share the same principal investigator — historically almost always a true merge.",

  // ---- severity ----
  high: "High severity — likely a real data-quality problem; prioritize.",
  medium: "Medium severity — worth checking but lower risk.",
  low: "Low severity — minor or informational.",

  // ---- pipeline / match / review statuses ----
  needs_investigation:
    "Open — the pipeline couldn't decide automatically and is waiting on a human/judge.",
  auto_accepted:
    "The scorer was confident enough to merge automatically (at/above the accept cutoff).",
  auto_rejected:
    "The scorer was confident these are different (below the reject cutoff).",
  human_review:
    "Confidence landed in the ambiguous band — routed to a human/judge instead of auto-deciding.",
  accepted_merge:
    "Reviewed and confirmed as the same entity — the pair was merged.",
  rejected_merge:
    "Reviewed and confirmed as different — the merge was declined.",
  accepted_split: "Reviewed and confirmed the record should be split apart.",
  rejected_split:
    "A split was proposed and declined — the record stays merged.",
  kept_provisional:
    "Admitted only provisionally — not yet promoted to a full golden record.",
  promoted_to_golden: "Passed admission and became a full golden record.",
  obsolete: "Superseded by a later decision or run — no longer actionable.",
  pending: "Queued for scoring/review; no decision recorded yet.",
  resolved: "Closed — a decision was recorded.",
  resolved_locally:
    "You resolved this in the app (recorded in the local decision log / applied to the model).",
  deferred:
    "You set this aside to revisit later — stays open but drops off the active list.",
  DISMISS: "Judge dismissed the flag — nothing to change.",
  CONFIRM_SPLIT: "Judge confirmed the record should be split.",
  CONFIRM_DUP: "Judge confirmed a duplicate.",
  FIELD_RESOLVED: "Judge resolved a conflicting field.",

  // ---- decision actions (buttons) ----
  accept_canonical:
    "Confirm the current golden record and its chosen canonical fields are correct — closes the flag as-is.",
  merge:
    "Confirm the two records are the same entity and combine them into one (updates the golden model + writes the audit trail).",
  keep_separate:
    "Confirm the records are different — decline the merge and keep them apart.",
  override:
    "Manually set the outcome against what the score or rule suggested.",
  defer:
    "Set aside to revisit later — the item stays open but leaves the active list.",
  not_an_issue: "Dismiss the flag — the pipeline was right; nothing to fix.",
  note: "Add a comment without recording a decision.",

  // ---- score-explorer metrics ----
  scored_pairs:
    "Every candidate site pair the scorer evaluated (after blocking).",
  auto_approved:
    "Pairs merged automatically — confidence at/above the observed accept cutoff.",
  sent_to_review:
    "Pairs whose confidence fell in the ambiguous band — routed to a human/judge.",
  merged_below_cutoff:
    "Pairs a human/judge merged even though they scored below the auto-accept cutoff — where judgment overrode the model.",
  confidence:
    "Calibrated probability (0–1) that the two records are the same site.",
  weighted_score:
    "Raw weighted sum of the signal scores, before it's calibrated into a confidence.",
  dead_band:
    "The confidence gap (~0.49–0.72) with essentially zero pairs — the model avoids the ambiguous middle, separating clear accepts from clear rejects.",
  accept_basket:
    "The named accept rule a pair matched to enter the merge zone — explains WHY it merged, not just how confident. Each code below summarizes the signals that rule keys on.",
  basket:
    "The named accept rule a pair matched to enter the merge zone — explains why it was accepted, not just its confidence. Hover a code to see what it keys on.",
  blocking_keys:
    "The keys that put these two records in the same candidate group so they'd even be compared (e.g. shared postal code or name token) — the pre-filter behind the pair.",

  // ---- accept-basket rule codes (descriptions derived from each basket's dominant
  //      signal profile in this cut; the pipeline's exact rule definitions aren't published) ----
  CORE_POSTAL:
    "Accept rule: matching core identity (site name) within the same postal code. The largest deterministic rule.",
  EXACT_NAME: "Accept rule: an exact site-name match.",
  EXACT_CORE:
    "Accept rule: an exact match on the core identity fields (name + institution).",
  ID_ADDR:
    "Accept rule: a shared external identifier backed by a matching address (and institution).",
  ID_NAME:
    "Accept rule: a shared external identifier backed by a matching name and institution.",
  ID_CORROB:
    "Accept rule: a shared external identifier corroborated by name/institution.",
  NI_strong: "Accept rule: a strong match on both name and institution.",
  NI_C: "Accept rule: name + institution match, corroborated by a contact signal (phone/email).",
  NI_exact_addr: "Accept rule: name + institution match with an exact address.",
  NIG: "Accept rule: name + institution + geolocation all match.",
  NIC_G:
    "Accept rule: name + institution match, confirmed by geolocation (and phone).",
  G_full:
    "Accept rule: same geolocation plus matching address and institution.",
  L1G_strong:
    "Accept rule: same geolocation with a strong name/institution match.",
  L3: "Accept rule: name, institution and address all align — a strong multi-field tier.",
  LC2: "Accept rule: matching phone with corroborating institution and address.",
  L2C1: "Accept rule: matching phone with supporting name/address.",
  C1G: "Accept rule: matching phone confirmed by geolocation.",
  SN: "Accept rule: same site number with matching email/address (and PI).",
  review_outcomes:
    "What human/judge review ultimately decided for golden-record pairs (merge / decline / still open).",
  signal_importance:
    "How much each of the 15 signals separates true merges from non-merges.",
  coverage:
    "Share of pairs that actually carry this signal (many signals are sparse).",
  mean_accepted:
    "Average value of this signal among auto-accepted (merged) pairs.",
  mean_rejected: "Average value of this signal among auto-rejected pairs.",
  discrimination:
    "Gap between the accepted and rejected means — a bigger gap means the signal separates the two classes better.",
  review_taxonomy:
    "The kind of ambiguity a reviewed pair represents, and how often each kind ends up merged.",
  judge_mix:
    "Which model/judge (or the pipeline) made each decision — a governance & calibration check. Judges even use different label vocabularies.",
  effective_status:
    "The live status after your local decisions are layered on top of the pipeline's status.",

  // ---- the 15 scoring signals ----
  "Site name": "String similarity of the two site names.",
  Institution: "Similarity of the parent institution / organization name.",
  Address: "Similarity of the postal addresses.",
  "Geo distance":
    "How close the two sites are geographically (from geocoded coordinates).",
  Phone: "Whether the phone numbers match.",
  "Email domain": "Whether the contact email domains match.",
  "NCT overlap":
    "Whether the two sites share the same clinical trials (NCT ids).",
  "Site continuity":
    "Evidence the same site persisted across trials/records over time.",
  "PI name": "Similarity of the principal investigator names.",
  "PI rarity":
    "How rare/distinctive the shared PI name is — a rare shared name is stronger evidence.",
  "Investigator network":
    "Whether the sites share investigators through a wider network.",
  IRB: "Whether the ethics board / IRB identifiers match.",
  "Site number": "Whether the sponsor-assigned site numbers match.",
  Coordinator: "Whether the study coordinator matches.",
  "External ID": "Whether an external org/registry identifier matches.",
};

// look up a definition tolerant of case / spacing / separators
export const define = (term?: string | null): string | undefined => {
  if (!term) return undefined;
  const t = String(term).trim();
  return (
    GLOSSARY[t] ??
    GLOSSARY[t.toLowerCase()] ??
    GLOSSARY[t.replace(/[\s/]+/g, "_").toLowerCase()]
  );
};
